import { useCallback, useEffect, useState } from "react";
import type { Project, Workspace } from "@racler/protocol";
import { useRelay } from "./relayContext.tsx";
import { removeRecent } from "./recent.ts";
import { forgetProject, lastProject } from "./projectContext.ts";

/**
 * Workspaces (approved locally, read-only here) and Projects of ONE machine, plus browse / add / create / remove for
 * Projects. Everything is enforced by the agent: the browser only ever names a (workspaceId, relative path).
 */
export function useProjectAdmin(machineId: string | null) {
  const relay = useRelay();
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!machineId) return;
    try {
      const [p, w] = await Promise.all([
        relay.machine(machineId, "system/projects.list", {}),
        relay.machine(machineId, "system/workspaces.list", {}),
      ]);
      setProjects(p.projects);
      setWorkspaces(w.workspaces);
      setError(null);
    } catch (e) {
      setProjects(null);
      setWorkspaces(null);
      setError((e as Error).message);
    }
  }, [relay, machineId]);

  useEffect(() => {
    setProjects(null);
    setWorkspaces(null);
    void reload();
  }, [reload]);

  const browse = useCallback(
    (workspaceId: string, path: string) => {
      if (!machineId) return Promise.reject(new Error("no machine"));
      return relay.machine(machineId, "system/projects.browse", { workspaceId, path });
    },
    [relay, machineId],
  );
  const add = useCallback(
    async (workspaceId: string, path: string, name?: string) => {
      if (!machineId) throw new Error("no machine");
      const r = await relay.machine(machineId, "system/projects.add", {
        workspaceId,
        path,
        ...(name ? { name } : {}),
      });
      await reload();
      return { project: r.project, git: r.git };
    },
    [relay, machineId, reload],
  );
  const create = useCallback(
    async (workspaceId: string, path: string, name: string) => {
      if (!machineId) throw new Error("no machine");
      const r = await relay.machine(machineId, "system/projects.create", { workspaceId, path, name });
      await reload();
      return { project: r.project, git: r.git };
    },
    [relay, machineId, reload],
  );
  const clone = useCallback(
    async (workspaceId: string, path: string, name: string, url: string) => {
      if (!machineId) throw new Error("no machine");
      const r = await relay.machine(machineId, "system/projects.clone", { workspaceId, path, name, url });
      await reload();
      return { project: r.project, git: r.git };
    },
    [relay, machineId, reload],
  );
  const remove = useCallback(
    async (projectId: string) => {
      if (!machineId) throw new Error("no machine");
      await relay.machine(machineId, "system/projects.remove", { projectId });
      setProjects((prev) => (prev ? prev.filter((p) => p.id !== projectId) : null));
      removeRecent(machineId, projectId);
      const lp = lastProject();
      if (lp && lp.machineId === machineId && lp.projectId === projectId) {
        forgetProject();
      }
      await reload();
    },
    [relay, machineId, reload],
  );

  return { machineId, projects, workspaces, error, reload, browse, add, create, clone, remove };
}
export type ProjectAdmin = ReturnType<typeof useProjectAdmin>;
